import type { editor } from 'monaco-editor';

export type DiffStats = {
  additions: number;
  deletions: number;
  modifications: number;
};

export const computeDiffStats = (
  lineChanges: editor.ILineChange[] | null
): DiffStats => {
  if (!lineChanges) return { additions: 0, deletions: 0, modifications: 0 };

  let additions = 0;
  let deletions = 0;
  let modifications = 0;

  for (const change of lineChanges) {
    const hasOriginal =
      change.originalStartLineNumber <= change.originalEndLineNumber;
    const hasModified =
      change.modifiedStartLineNumber <= change.modifiedEndLineNumber;

    if (hasOriginal && hasModified) {
      // Modification: lines exist in both original and modified
      // Count the max of the two ranges as "modified lines"
      const originalLines =
        change.originalEndLineNumber - change.originalStartLineNumber + 1;
      const modifiedLines =
        change.modifiedEndLineNumber - change.modifiedStartLineNumber + 1;
      modifications += Math.min(originalLines, modifiedLines);
      // If one side has more lines, those are pure additions/deletions
      if (modifiedLines > originalLines) {
        additions += modifiedLines - originalLines;
      } else if (originalLines > modifiedLines) {
        deletions += originalLines - modifiedLines;
      }
    } else if (hasModified) {
      // Pure addition: no original lines
      additions +=
        change.modifiedEndLineNumber - change.modifiedStartLineNumber + 1;
    } else if (hasOriginal) {
      // Pure deletion: no modified lines
      deletions +=
        change.originalEndLineNumber - change.originalStartLineNumber + 1;
    }
  }

  return { additions, deletions, modifications };
};
